import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LunchMoneyAPI } from '../shared/api/lunchMoney';
import { Adjustments } from '../shared/components/Adjustments';
import { CategorySettings } from '../shared/components/CategorySettings';
import { PlannerTable } from '../shared/components/PlannerTable';
import type { Asset } from '../shared/types/asset';
import type { Budget } from '../shared/types/budget';
import type { Category } from '../shared/types/category';
import type { Goal } from '../shared/types/goal';
import type { WeekData } from '../shared/types/planner';
import type { RecurringItem } from '../shared/types/recurring';
import { deriveExpectedTransactionsFromBudgets } from '../shared/utils/budgetUtils';
import { calculatePlannerData, type CalculationOptions } from '../shared/utils/calculations';
import { generateWeekPeriods } from '../shared/utils/dateUtils';
import './App.css';

type Page = 'planner' | 'settings' | 'adjustments' | 'help' | 'about';

const API_KEY_STORAGE_KEY = 'lunchmoney_api_key';
const ADJUSTMENTS_STORAGE_KEY = 'planner_adjustments';
const STARTING_BALANCE_KEY = 'planner_starting_balance';
const BUFFER_VALUE_KEY = 'planner_buffer_value';
const SOURCE_ORDER_KEY = 'planner_source_order';
const GOALS_KEY = 'planner_goals';
const WEEK_START_DAY_KEY = 'planner_week_start_day';
const SANDWICH_SUPPORT_URL = 'https://buy.stripe.com/fZuaEYg7U0dM6DHeKl2go00';
const PAGE_PATHS: Record<Page, string> = {
  planner: '/planner',
  settings: '/settings',
  adjustments: '/adjustments',
  help: '/docs',
  about: '/about',
};

function filterDepositoryPlaidAccounts(plaidAccounts: any[]): any[] {
  return plaidAccounts.filter((account) => {
    const isDepository = account.type === 'cash' || account.type === 'depository';
    const statusLower = account.status?.toLowerCase() || '';
    const excludedStatuses = ['inactive', 'closed', 'error', 'not found', 'not supported', 'relink'];
    const isNotExcluded = !excludedStatuses.includes(statusLower);
    const isNotOld =
      !account.name?.toLowerCase().includes('(old)') && !account.name?.toLowerCase().includes(' old');
    return isDepository && isNotExcluded && isNotOld;
  });
}

function normalizePathname(pathname: string): string {
  return pathname.replace(/\/+$/, '') || '/';
}

function getPageFromPathname(pathname: string): Page | null {
  const normalizedPath = normalizePathname(pathname);

  for (const [page, pagePath] of Object.entries(PAGE_PATHS) as [Page, string][]) {
    if (normalizedPath === pagePath) {
      return page;
    }
  }

  return null;
}

function getCurrentPage(pathname: string, hasApiKey: boolean): Page {
  const requestedPage = getPageFromPathname(pathname);

  if (requestedPage) {
    return !hasApiKey && requestedPage === 'planner' ? 'settings' : requestedPage;
  }

  return hasApiKey ? 'planner' : 'settings';
}

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [apiKey, setApiKey] = useState<string>(() => {
    const stored = localStorage.getItem(API_KEY_STORAGE_KEY);
    if (stored) return stored;
    const envKey = import.meta.env.VITE_LUNCHMONEY_API_KEY as string | undefined;
    if (envKey && envKey !== 'your_api_key_here' && envKey.trim()) {
      return envKey.trim();
    }
    return '';
  });
  const [weeks, setWeeks] = useState<WeekData[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currency, setCurrency] = useState<string>('USD');
  const [adjustments, setAdjustments] = useState<Record<string, number>>(() => {
    const stored = localStorage.getItem(ADJUSTMENTS_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  });
  const [startingBalance, setStartingBalance] = useState<number>(() => {
    const stored = localStorage.getItem(STARTING_BALANCE_KEY);
    return stored ? parseFloat(stored) : 0;
  });
  const [goals, setGoals] = useState<Goal[]>(() => {
    const stored = localStorage.getItem(GOALS_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Ensure we have at least 4 goals (Buffer + 3 placeholders)
        const defaultGoals: Goal[] = [
          { name: 'Buffer', value: 2000 },
          { name: '', value: null },
          { name: '', value: null },
          { name: '', value: null },
        ];
        return parsed.length >= 4 ? parsed : [...parsed, ...defaultGoals.slice(parsed.length)];
      } catch {
        // Fall through to default
      }
    }
    // Default: Buffer goal + 3 empty placeholders
    return [
      { name: 'Buffer', value: 2000 },
      { name: '', value: null },
      { name: '', value: null },
      { name: '', value: null },
    ];
  });

  // Keep bufferValue for backward compatibility, but derive from goals
  const bufferValue = goals[0]?.value || 2000;
  const [sourceOrder, setSourceOrder] = useState<string[]>(() => {
    const stored = localStorage.getItem(SOURCE_ORDER_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // Validate that it contains valid source names
        const validSources = ['budgets', 'recurring'];
        return parsed.filter((s: string) => validSources.includes(s));
      } catch {
        // Fall through to default
      }
    }
    // Default order: recurring first (for backward compatibility)
    return ['recurring', 'budgets'];
  });
  const [weekStartDay, setWeekStartDay] = useState<number>(() => {
    const stored = localStorage.getItem(WEEK_START_DAY_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      // Validate that it's a valid day (0-6)
      if (!isNaN(parsed) && parsed >= 0 && parsed <= 6) {
        return parsed;
      }
    }
    // Default to Sunday (0)
    return 0;
  });
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [openHelpPopup, setOpenHelpPopup] = useState<string | null>(null);
  const [popupPosition, setPopupPosition] = useState<{ top: number; right: number } | null>(null);
  const currentPage = getCurrentPage(location.pathname, Boolean(apiKey));

  // Close help popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Don't close if clicking on a help icon or inside a popup
      if (!target.closest('[data-help-popup]') && !target.closest('[data-help-content]')) {
        setOpenHelpPopup(null);
        setPopupPosition(null);
      }
    };
    if (openHelpPopup) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [openHelpPopup]);

  // Migrate old bufferValue to goals if needed
  useEffect(() => {
    const oldBufferValue = localStorage.getItem(BUFFER_VALUE_KEY);
    if (oldBufferValue && goals[0]?.name === 'Buffer' && goals[0]?.value === 2000) {
      const bufferNum = parseFloat(oldBufferValue);
      if (!isNaN(bufferNum)) {
        const newGoals = [...goals];
        newGoals[0] = { name: 'Buffer', value: bufferNum };
        setGoals(newGoals);
        localStorage.setItem(GOALS_KEY, JSON.stringify(newGoals));
      }
    }
  }, []);


  useEffect(() => {
    if (apiKey) {
      loadData();
    }
  }, [apiKey]);

  useEffect(() => {
    const canonicalPath = PAGE_PATHS[currentPage];
    const normalizedPath = normalizePathname(location.pathname);

    if (normalizedPath !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
  }, [currentPage, location.pathname, navigate]);

  useEffect(() => {
    if (apiKey && weeks.length > 0) {
      recalculate();
    }
  }, [adjustments, startingBalance, bufferValue, sourceOrder, goals, weekStartDay]);

  // Recalculate when category settings change (listen to localStorage changes)
  useEffect(() => {
    const handleCategorySettingsChange = () => {
      if (apiKey && weeks.length > 0) {
        recalculate();
      }
    };

    window.addEventListener('categorySettingsChanged', handleCategorySettingsChange);
    return () => {
      window.removeEventListener('categorySettingsChanged', handleCategorySettingsChange);
    };
  }, [apiKey, weeks.length]);

  // Recalculate when adjustments change (listen to localStorage changes)
  useEffect(() => {
    const handleAdjustmentsChange = () => {
      if (apiKey && weeks.length > 0) {
        recalculate();
      }
    };

    window.addEventListener('adjustmentsChanged', handleAdjustmentsChange);
    return () => {
      window.removeEventListener('adjustmentsChanged', handleAdjustmentsChange);
    };
  }, [apiKey, weeks.length]);

  const calculateCashBalance = async (
    plaidAccounts: any[] = [],
    assets: Asset[] = []
  ): Promise<number> => {
    const depositoryAccounts = filterDepositoryPlaidAccounts(plaidAccounts);

    // Sum plaid account balances
    const accountBalanceTotal = depositoryAccounts.reduce((sum, account) => {
      let balance = 0;
      if (account.to_base !== null && account.to_base !== undefined) {
        balance = account.to_base;
      } else if (account.balance) {
        balance = parseFloat(account.balance) || 0;
      }
      return sum + balance;
    }, 0);

    // Filter assets: cash or depository type (check type_name for "cash", "depository", "checking", "savings", etc.)
    const cashAssets = assets.filter(asset => {
      const typeLower = asset.type_name?.toLowerCase() || '';
      const isCashType = typeLower === 'cash' ||
                        typeLower === 'depository' ||
                        typeLower === 'checking' ||
                        typeLower === 'savings' ||
                        typeLower === 'bank account';
      // Exclude closed accounts
      const isNotClosed = !asset.closed_on;
      // Exclude accounts with "Old" in the name
      const isNotOld = !asset.name?.toLowerCase().includes('(old)') && !asset.name?.toLowerCase().includes(' old');
      return isCashType && isNotClosed && isNotOld;
    });

    // Sum asset balances
    const assetBalanceTotal = cashAssets.reduce((sum, asset) => {
      let balance = 0;
      if (asset.to_base !== null && asset.to_base !== undefined) {
        balance = asset.to_base;
      } else if (asset.balance) {
        balance = parseFloat(asset.balance) || 0;
      }
      return sum + balance;
    }, 0);

    const cashBalance = accountBalanceTotal + assetBalanceTotal;
    console.log(
      `Cash balance: $${cashBalance.toFixed(2)} (${depositoryAccounts.length} Plaid accounts, ${cashAssets.length} manual assets)`
    );
    return cashBalance;
  };


  const loadData = async () => {
    if (!apiKey) return;

    setLoading(true);
    setError(null);

    try {
      const api = new LunchMoneyAPI(apiKey);

      // Fetch user info to get primary currency
      const userInfo = await api.getUser();
      setCurrency(userInfo.primary_currency || 'USD');

      // Generate 75 weeks starting from this week
      const today = new Date();
      const weekPeriods = generateWeekPeriods(today, 75, weekStartDay);

      // Fetch data
      const startDate = weekPeriods[0].date;
      const endDateStr = weekPeriods[weekPeriods.length - 1].date;

      // Calculate date range for budgets (monthly budgets need start/end of months)
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDateStr);
      const budgetStartDate = new Date(startDateObj.getFullYear(), startDateObj.getMonth(), 1).toISOString().split('T')[0];
      const budgetEndDate = new Date(endDateObj.getFullYear(), endDateObj.getMonth() + 1, 0).toISOString().split('T')[0];

      const fetchPromises: [
        Promise<Category[]>,
        Promise<RecurringItem[]>,
        Promise<any[]>,
        Promise<Asset[]>,
        Promise<{ budgets: Budget[] } | null>
      ] = [
        api.getCategories(),
        api.getRecurringItems(startDate, endDateStr),
        api.getPlaidAccounts(),
        api.getAssets(),
        sourceOrder.includes('budgets') ? api.getBudgets(budgetStartDate, budgetEndDate) : Promise.resolve(null),
      ];

      const [fetchedCategories, recurringItems, plaidAccounts, assets, budgetsResponse] = await Promise.all(fetchPromises);

      // Handle both array response and object with budgets property
      const budgetsArray = Array.isArray(budgetsResponse)
        ? budgetsResponse
        : budgetsResponse?.budgets || [];

      if (budgetsArray.length > 0 && sourceOrder.includes('budgets')) {
        console.log(`[Budget] Found ${budgetsArray.length} budgets`);
      }

      if (recurringItems.length > 0 && sourceOrder.includes('recurring')) {
        console.log(`[Recurring] Found ${recurringItems.length} recurring items`);
      }

      // Store categories in state
      setCategories(fetchedCategories);

      // Reset starting balance from cash accounts (Plaid + Assets) only
      const cashBalance = await calculateCashBalance(plaidAccounts || [], assets || []);
      setStartingBalance(cashBalance);
      localStorage.setItem(STARTING_BALANCE_KEY, cashBalance.toString());

      // Derive expected transactions from budgets if enabled
      // Pass the actual week dates to ensure budget transactions match planner weeks exactly
      const weekDates = weekPeriods.map(wp => wp.date);
      const budgetExpectedTransactions = sourceOrder.includes('budgets') && budgetsArray.length > 0
        ? deriveExpectedTransactionsFromBudgets(
            budgetsArray,
            fetchedCategories,
            weekPeriods[0].start,
            weekPeriods[weekPeriods.length - 1].end,
            weekDates
          )
        : [];

      // Summary logging for budget transactions
      if (sourceOrder.includes('budgets')) {
        if (budgetExpectedTransactions.length === 0 && budgetsArray.length > 0) {
          console.warn('[Budget] WARNING: Budgets are enabled but no budget transactions were created');
        }
      }

      // Summary logging for recurring items
      if (sourceOrder.includes('recurring')) {
        // Count total occurrences across all recurring items
        const totalOccurrences = recurringItems.reduce((sum, item) => {
          return sum + Object.keys(item.occurrences || {}).length;
        }, 0);
        if (totalOccurrences === 0 && recurringItems.length > 0) {
          console.warn('[Recurring] WARNING: Recurring items are enabled but no occurrences found in date range');
        } else if (recurringItems.length > 0) {
          console.log(`[Recurring] Found ${totalOccurrences} occurrences across ${recurringItems.length} recurring items`);
        }
      }

      // Calculate planner data
      const calculationOptions: CalculationOptions = {
        useBudgets: sourceOrder.includes('budgets'),
        useRecurring: sourceOrder.includes('recurring'),
        budgetOverridesRecurring: sourceOrder.indexOf('budgets') < sourceOrder.indexOf('recurring'),
      };

      const calculatedWeeks = calculatePlannerData(
        weekPeriods,
        recurringItems,
        fetchedCategories,
        cashBalance,
        adjustments,
        bufferValue,
        budgetExpectedTransactions,
        calculationOptions,
        assets
      );

      setWeeks(calculatedWeeks);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const recalculate = async () => {
    if (!apiKey || weeks.length === 0) return;

    try {
      const api = new LunchMoneyAPI(apiKey);
      const today = new Date();
      const weekPeriods = generateWeekPeriods(today, 75, weekStartDay);

      const startDate = weekPeriods[0].date;
      const endDateStr = weekPeriods[weekPeriods.length - 1].date;

      // Calculate date range for budgets (monthly budgets need start/end of months)
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDateStr);
      const budgetStartDate = new Date(startDateObj.getFullYear(), startDateObj.getMonth(), 1).toISOString().split('T')[0];
      const budgetEndDate = new Date(endDateObj.getFullYear(), endDateObj.getMonth() + 1, 0).toISOString().split('T')[0];

      const fetchPromises: [
        Promise<Category[]>,
        Promise<RecurringItem[]>,
        Promise<Asset[]>,
        Promise<{ budgets: Budget[] } | null>
      ] = [
        api.getCategories(),
        api.getRecurringItems(startDate, endDateStr),
        api.getAssets(),
        sourceOrder.includes('budgets') ? api.getBudgets(budgetStartDate, budgetEndDate) : Promise.resolve(null),
      ];

      const [fetchedCategories, recurringItems, assets, budgetsResponse] = await Promise.all(fetchPromises);

      // Update categories in state
      setCategories(fetchedCategories);

      // Derive expected transactions from budgets if enabled
      // Pass the actual week dates to ensure budget transactions match planner weeks exactly
      const weekDates = weekPeriods.map(wp => wp.date);

      // Handle both array response and object with budgets property
      const budgetsArray = Array.isArray(budgetsResponse)
        ? budgetsResponse
        : budgetsResponse?.budgets || [];

      if (budgetsArray.length > 0 && sourceOrder.includes('budgets')) {
        console.log(`[Budget] Found ${budgetsArray.length} budgets`);
      }

      if (recurringItems.length > 0 && sourceOrder.includes('recurring')) {
        console.log(`[Recurring] Found ${recurringItems.length} recurring items`);
      }

      const budgetExpectedTransactions = sourceOrder.includes('budgets') && budgetsArray.length > 0
        ? deriveExpectedTransactionsFromBudgets(
            budgetsArray,
            fetchedCategories,
            weekPeriods[0].start,
            weekPeriods[weekPeriods.length - 1].end,
            weekDates
          )
        : [];

      // Summary logging for budget transactions
      if (sourceOrder.includes('budgets')) {
        if (budgetExpectedTransactions.length === 0 && budgetsArray.length > 0) {
          console.warn('[Budget] WARNING: Budgets are enabled but no budget transactions were created');
        }
      }

      // Summary logging for recurring items
      if (sourceOrder.includes('recurring')) {
        // Count total occurrences across all recurring items
        const totalOccurrences = recurringItems.reduce((sum, item) => {
          return sum + Object.keys(item.occurrences || {}).length;
        }, 0);
        if (totalOccurrences === 0 && recurringItems.length > 0) {
          console.warn('[Recurring] WARNING: Recurring items are enabled but no occurrences found in date range');
        } else if (recurringItems.length > 0) {
          console.log(`[Recurring] Found ${totalOccurrences} occurrences across ${recurringItems.length} recurring items`);
        }
      }

      const calculationOptions: CalculationOptions = {
        useBudgets: sourceOrder.includes('budgets'),
        useRecurring: sourceOrder.includes('recurring'),
        budgetOverridesRecurring: sourceOrder.indexOf('budgets') < sourceOrder.indexOf('recurring'),
      };

      const calculatedWeeks = calculatePlannerData(
        weekPeriods,
        recurringItems,
        fetchedCategories,
        startingBalance,
        adjustments,
        bufferValue,
        budgetExpectedTransactions,
        calculationOptions,
        assets
      );

      setWeeks(calculatedWeeks);
    } catch (err) {
      console.error('Error recalculating:', err);
    }
  };

  const handleApiKeySave = () => {
    if (apiKey.trim()) {
      localStorage.setItem(API_KEY_STORAGE_KEY, apiKey.trim());
      navigate(PAGE_PATHS.planner);
      loadData();
    }
  };

  const handleAdjustmentChange = (date: string, value: number) => {
    const newAdjustments = { ...adjustments, [date]: value };
    setAdjustments(newAdjustments);
    localStorage.setItem(ADJUSTMENTS_STORAGE_KEY, JSON.stringify(newAdjustments));
  };

  const handleStartingBalanceChange = (value: number) => {
    setStartingBalance(value);
    localStorage.setItem(STARTING_BALANCE_KEY, value.toString());
  };

  const handleWeekStartDayChange = (value: number) => {
    setWeekStartDay(value);
    localStorage.setItem(WEEK_START_DAY_KEY, value.toString());
    // Reload data to regenerate weeks with new start day
    if (apiKey && weeks.length > 0) {
      recalculate();
    }
  };

  const handleGoalChange = (index: number, field: 'name' | 'value', newValue: string | number) => {
    const newGoals = [...goals];
    newGoals[index] = {
      ...newGoals[index],
      [field]: field === 'value' ? (newValue === '' ? null : Number(newValue)) : newValue,
    };
    setGoals(newGoals);
    localStorage.setItem(GOALS_KEY, JSON.stringify(newGoals));

    // Also update legacy bufferValue storage for backward compatibility
    if (index === 0 && field === 'value') {
      localStorage.setItem(BUFFER_VALUE_KEY, newValue.toString());
    }
  };

  const handleSourceOrderChange = (newOrder: string[]) => {
    setSourceOrder(newOrder);
    localStorage.setItem(SOURCE_ORDER_KEY, JSON.stringify(newOrder));
  };

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    const target = e.currentTarget as HTMLElement;
    target.style.opacity = '0.5';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnd = (e: React.DragEvent) => {
    const target = e.currentTarget as HTMLElement;
    target.style.opacity = '1';
    setDraggedIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (draggedIndex === null) return;
    const dragIndex = draggedIndex;

    if (dragIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    const newOrder = [...sourceOrder];
    const [removed] = newOrder.splice(dragIndex, 1);
    newOrder.splice(dropIndex, 0, removed);

    handleSourceOrderChange(newOrder);
    setDraggedIndex(null);
  };

  const renderPage = () => {
    if (currentPage === 'settings') {
      return (
        <div className="config-container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
            <h1 style={{ margin: 0 }}>Meal Ticket Configuration</h1>
          </div>
          <div className="config-form">
            <div style={{ display: 'flex', gap: '20px' }}>
              <div style={{ width: 'calc(50% - 10px)', padding: '15px', border: '1px solid #ddd', borderRadius: '5px', backgroundColor: '#f9f9f9', boxSizing: 'border-box', overflow: 'visible', position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                  <h3 style={{ marginTop: 0, marginBottom: 0 }}>API Configuration</h3>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <a
                      href="https://my.lunchmoney.app/developers"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: '0.85em',
                        color: '#0366d6',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.textDecoration = 'underline';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.textDecoration = 'none';
                      }}
                    >
                      Get an API key
                      <span style={{ fontSize: '0.9em' }}>↗</span>
                    </a>
                    <div style={{ position: 'relative' }} data-help-popup>
                      <span
                        onClick={(e) => {
                          e.stopPropagation();
                          if (openHelpPopup === 'api') {
                            setOpenHelpPopup(null);
                            setPopupPosition(null);
                          } else {
                            const rect = e.currentTarget.getBoundingClientRect();
                            setPopupPosition({ top: rect.bottom + 5, right: window.innerWidth - rect.right });
                            setOpenHelpPopup('api');
                          }
                        }}
                        style={{
                          cursor: 'pointer',
                          fontSize: '16px',
                          color: '#666',
                          backgroundColor: '#fff',
                          border: '1px solid #ddd',
                          borderRadius: '50%',
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          lineHeight: 1,
                          fontWeight: 600,
                          flexShrink: 0,
                          userSelect: 'none'
                        }}
                      >
                        ?
                      </span>
                    </div>
                    {openHelpPopup === 'api' && popupPosition && (
                      <div
                        data-help-content
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          position: 'fixed',
                          top: `${popupPosition.top}px`,
                          right: `${popupPosition.right}px`,
                          backgroundColor: '#fff',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          padding: '12px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                          zIndex: 10000,
                          width: '300px',
                          fontSize: '14px',
                          lineHeight: '1.5',
                          color: '#24292e'
                        }}
                      >
                        Connect Meal Ticket to your <a href="https://lunchmoney.app/" target="_blank" rel="noopener noreferrer" style={{ color: '#0366d6', textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}>Lunch Money</a> account by entering your API key. Your API key allows Meal Ticket to fetch your budgets, recurring transactions, categories, and account balances. Click 'Save & Load Data' after entering your key to fetch and display your financial data. Your API key is stored locally in your browser and never shared with any server except <a href="https://lunchmoney.app/" target="_blank" rel="noopener noreferrer" style={{ color: '#0366d6', textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}>Lunch Money</a>'s official API.
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', width: '100%', minWidth: 0 }}>
                  <label style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <span style={{ fontWeight: 500 }}>Lunch Money API Key:</span>
                    <input
                      type="password"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="Enter your API key"
                      className="api-key-input"
                      style={{ width: '100%', boxSizing: 'border-box', minWidth: 0 }}
                    />
                  </label>
                  <button onClick={handleApiKeySave} className="save-button" style={{ marginBottom: 0, padding: '8px 16px', fontSize: '14px', flexShrink: 0, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px', lineHeight: '1.5' }}>
                    💾 Save & Load Data
                  </button>
                </div>
              </div>
              <div style={{ flex: 1, padding: 0, border: 'none', backgroundColor: 'transparent' }}></div>
            </div>
            <div style={{ display: 'flex', gap: '20px', marginTop: '20px' }}>
              <div style={{ width: 'calc(50% - 10px)', padding: '15px', border: '1px solid #ddd', borderRadius: '5px', backgroundColor: '#f9f9f9', boxSizing: 'border-box', overflow: 'visible', position: 'relative' }}>
                <h3 style={{ marginTop: 0, marginBottom: '8px' }}>General</h3>
                <div style={{ position: 'absolute', top: '15px', right: '15px' }} data-help-popup>
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      if (openHelpPopup === 'balance') {
                        setOpenHelpPopup(null);
                        setPopupPosition(null);
                      } else {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setPopupPosition({ top: rect.bottom + 5, right: window.innerWidth - rect.right });
                        setOpenHelpPopup('balance');
                      }
                    }}
                    style={{
                      cursor: 'pointer',
                      fontSize: '16px',
                      color: '#666',
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                      fontWeight: 600,
                      userSelect: 'none'
                    }}
                  >
                    ?
                  </span>
                </div>
                {openHelpPopup === 'balance' && popupPosition && (
                  <div
                    data-help-content
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'fixed',
                      top: `${popupPosition.top}px`,
                      right: `${popupPosition.right}px`,
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      padding: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 10000,
                      width: '300px',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      color: '#24292e'
                    }}
                  >
                    Your starting balance is <strong>what&apos;s in your accounts now</strong>—Cash and Depository balances from <a href="https://lunchmoney.app/" target="_blank" rel="noopener noreferrer" style={{ color: '#0366d6', textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}>Lunch Money</a> plus manual cash assets (future-dated txn amounts are not added). You can override it here. This balance is the baseline for all cash flow projections.
                  </div>
                )}
                <p style={{ fontSize: '0.9em', color: '#666', margin: '0 0 15px 0' }}>Default value is derived from Cash and Depository accounts, but may be overridden locally</p>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '15px', alignItems: 'flex-start' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <span style={{ fontWeight: 500 }}>Starting Balance:</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span>$</span>
                      <input
                        type="number"
                        step="1"
                        value={Math.round(startingBalance)}
                        onChange={(e) => handleStartingBalanceChange(parseInt(e.target.value) || 0)}
                        placeholder="0"
                        className="balance-input"
                      />
                    </div>
                  </label>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <span style={{ fontWeight: 500 }}>Start on Day of Week:</span>
                    <select
                      value={weekStartDay}
                      onChange={(e) => handleWeekStartDayChange(parseInt(e.target.value))}
                      style={{
                        padding: '8px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        fontSize: '14px',
                        backgroundColor: '#fff',
                        cursor: 'pointer'
                      }}
                    >
                      <option value={0}>Sunday</option>
                      <option value={1}>Monday</option>
                      <option value={2}>Tuesday</option>
                      <option value={3}>Wednesday</option>
                      <option value={4}>Thursday</option>
                      <option value={5}>Friday</option>
                      <option value={6}>Saturday</option>
                    </select>
                  </label>
                </div>
              </div>
              <div style={{ flex: 1, padding: '15px', border: '1px solid #ddd', borderRadius: '5px', backgroundColor: '#f9f9f9', position: 'relative' }}>
                <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Sources</h3>
                <div style={{ position: 'absolute', top: '15px', right: '15px' }} data-help-popup>
                  <span
                    onClick={(e) => {
                      e.stopPropagation();
                      if (openHelpPopup === 'sources') {
                        setOpenHelpPopup(null);
                        setPopupPosition(null);
                      } else {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setPopupPosition({ top: rect.bottom + 5, right: window.innerWidth - rect.right });
                        setOpenHelpPopup('sources');
                      }
                    }}
                    style={{
                      cursor: 'pointer',
                      fontSize: '16px',
                      color: '#666',
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                      fontWeight: 600,
                      userSelect: 'none'
                    }}
                  >
                    ?
                  </span>
                </div>
                {openHelpPopup === 'sources' && popupPosition && (
                  <div
                    data-help-content
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'fixed',
                      top: `${popupPosition.top}px`,
                      right: `${popupPosition.right}px`,
                      backgroundColor: '#fff',
                      border: '1px solid #ddd',
                      borderRadius: '6px',
                      padding: '12px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 10000,
                      width: '300px',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      color: '#24292e'
                    }}
                  >
                    Cash flow projections can use budgets, recurring transactions, or both. Drag items to reorder them—the first item takes precedence when both sources have data for the same category. For example, if budgets is first and you have a budget for 'Groceries', that budget will be used even if you also have a recurring transaction for 'Groceries'. Use this to control which data source Meal Ticket prioritizes when generating projections.
                  </div>
                )}
                <p style={{ fontSize: '0.9em', color: '#666', marginBottom: '10px', marginTop: 0 }}>
                  Drag to reorder (first item takes precedence)
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {sourceOrder.map((source, index) => {
                    const sourceNames: Record<string, string> = {
                      budgets: 'Budgets',
                      recurring: 'Recurring Transactions',
                    };
                    return (
                      <div
                        key={source}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragOver={handleDragOver}
                        onDragEnd={handleDragEnd}
                        onDrop={(e) => handleDrop(e, index)}
                        style={{
                          padding: '10px',
                          backgroundColor: draggedIndex === index ? '#e3f2fd' : '#fff',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          cursor: 'grab',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          userSelect: 'none',
                          transition: 'background-color 0.2s, opacity 0.2s',
                          opacity: draggedIndex === index ? 0.5 : 1,
                        }}
                        onMouseEnter={(e) => {
                          if (draggedIndex !== index) {
                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (draggedIndex !== index) {
                            e.currentTarget.style.backgroundColor = '#fff';
                          }
                        }}
                        onMouseDown={(e) => {
                          (e.currentTarget as HTMLElement).style.cursor = 'grabbing';
                        }}
                        onMouseUp={(e) => {
                          (e.currentTarget as HTMLElement).style.cursor = 'grab';
                        }}
                      >
                        <span style={{ fontSize: '18px', color: '#999' }}>☰</span>
                        <span style={{ flex: 1 }}>{sourceNames[source] || source}</span>
                        <span style={{ fontSize: '0.85em', color: '#666' }}>
                          {index === 0 ? '(highest priority)' : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {apiKey && categories.length > 0 && (
            <div style={{ display: 'flex', gap: '20px', marginTop: '20px', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <CategorySettings
                  categories={categories}
                  onClose={() => navigate(PAGE_PATHS.planner)}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div className="category-settings-container" style={{ backgroundColor: '#f9f9f9', position: 'relative' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', paddingBottom: '16px', borderBottom: '2px solid #e1e4e8' }}>
                    <div>
                      <h3 style={{ marginTop: 0, marginBottom: '8px' }}>Goals</h3>
                      <p style={{ fontSize: '0.9em', color: '#666', margin: 0 }}>Goals with names and values will appear as horizontal lines on the visualization</p>
                    </div>
                  </div>
                  <div style={{ position: 'absolute', top: '15px', right: '15px' }} data-help-popup>
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        if (openHelpPopup === 'goals') {
                          setOpenHelpPopup(null);
                          setPopupPosition(null);
                        } else {
                          const rect = e.currentTarget.getBoundingClientRect();
                          setPopupPosition({ top: rect.bottom + 5, right: window.innerWidth - rect.right });
                          setOpenHelpPopup('goals');
                        }
                      }}
                      style={{
                        cursor: 'pointer',
                        fontSize: '16px',
                        color: '#666',
                        backgroundColor: '#fff',
                        border: '1px solid #ddd',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1,
                        fontWeight: 600,
                        userSelect: 'none'
                      }}
                    >
                      ?
                    </span>
                  </div>
                  {openHelpPopup === 'goals' && popupPosition && (
                    <div
                      data-help-content
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: 'fixed',
                        top: `${popupPosition.top}px`,
                        right: `${popupPosition.right}px`,
                        backgroundColor: '#fff',
                        border: '1px solid #ddd',
                        borderRadius: '6px',
                        padding: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        zIndex: 10000,
                        width: '300px',
                        fontSize: '14px',
                        lineHeight: '1.5',
                        color: '#24292e'
                      }}
                    >
                      Set financial goals as target balance amounts. Goals with names and values will appear as horizontal orange lines on the cash flow visualization, helping you see when your projected balance reaches or crosses these targets. The 'Buffer' goal represents your minimum comfortable balance buffer. Add additional goals to track other financial milestones like emergency fund targets or savings goals.
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                    {goals.map((goal, index) => (
                      <div key={index} style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <input
                            type="text"
                            value={goal.name}
                            onChange={(e) => handleGoalChange(index, 'name', e.target.value)}
                            placeholder={index === 0 ? 'Buffer' : `Goal ${index}`}
                            style={{
                              flex: 1,
                              padding: '8px 12px',
                              border: '1px solid #d1d5da',
                              borderRadius: '6px',
                              fontSize: '14px',
                              backgroundColor: '#fff',
                            }}
                          />
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <span>$</span>
                            <input
                              type="number"
                              step="1"
                              value={goal.value !== null ? Math.round(goal.value) : ''}
                              onChange={(e) => handleGoalChange(index, 'value', e.target.value === '' ? '' : parseInt(e.target.value) || 0)}
                              placeholder={index === 0 ? '2000' : 'Amount'}
                              style={{
                                width: '120px',
                                padding: '8px 12px',
                                border: '1px solid #d1d5da',
                                borderRadius: '6px',
                                fontSize: '14px',
                                backgroundColor: '#fff',
                              }}
                            />
                          </div>
                          <button
                            onClick={() => {
                              const newGoals = [...goals];
                              newGoals[index] = { name: '', value: null };
                              setGoals(newGoals);
                              localStorage.setItem(GOALS_KEY, JSON.stringify(newGoals));
                            }}
                            style={{
                              padding: '6px 10px',
                              backgroundColor: '#f6f8fa',
                              color: '#586069',
                              border: '1px solid #d1d5da',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: 700,
                              minWidth: '32px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'background-color 0.2s, border-color 0.2s',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#e1e4e8';
                              e.currentTarget.style.borderColor = '#c6cbd1';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = '#f6f8fa';
                              e.currentTarget.style.borderColor = '#d1d5da';
                            }}
                            title="Clear goal"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      );
    }

    if (currentPage === 'adjustments') {
      return (
        <div className="config-container">
          {categories.length > 0 ? (
            <Adjustments categories={categories} />
          ) : apiKey ? (
            <div className="loading">Loading categories...</div>
          ) : (
            <div className="error">Please configure your API key in Settings first.</div>
          )}
        </div>
      );
    }

    if (currentPage === 'help') {
      return (
        <div className="config-container">
          <div style={{ lineHeight: '1.6', color: '#24292e', maxWidth: '900px' }}>
            <h1 style={{ marginTop: 0 }}>Help & Documentation</h1>

            <h2>Table of Contents</h2>
            <ul style={{ lineHeight: '2' }}>
              <li><a href="#getting-started" style={{ color: '#0366d6', textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}>Getting Started</a></li>
              <li><a href="#terms" style={{ color: '#0366d6', textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}>Terms & Definitions</a></li>
              <li><a href="#starting-balance-recurring" style={{ color: '#0366d6', textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}>Starting Balance & Recurring Detail</a></li>
              <li><a href="#features" style={{ color: '#0366d6', textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}>Features</a></li>
              <li><a href="#tips" style={{ color: '#0366d6', textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}>Tips & Best Practices</a></li>
              <li><a href="#how-to" style={{ color: '#0366d6', textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}>How-To Guides</a></li>
            </ul>

            <section id="getting-started" style={{ marginTop: '40px' }}>
              <h2>Getting Started</h2>
              <h3>Initial Setup</h3>
              <ol>
                <li><strong>Get your Lunch Money API Key:</strong> Navigate to <a href="https://my.lunchmoney.app/developers" target="_blank" rel="noopener noreferrer" style={{ color: '#0366d6', textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}>Lunch Money Developers</a> and create an API key.</li>
                <li><strong>Enter your API Key:</strong> Go to Settings and paste your API key in the "Lunch Money API Key" field, or set <code style={{ backgroundColor: '#f6f8fa', padding: '2px 6px', borderRadius: '3px' }}>VITE_LUNCHMONEY_API_KEY</code> in a <code style={{ backgroundColor: '#f6f8fa', padding: '2px 6px', borderRadius: '3px' }}>.env.local</code> file for local development (used only when no key is saved in the browser; restart the dev server after changing env vars).</li>
                <li><strong>Save & Load Data:</strong> Click "Save & Load Data" to fetch your financial data from Lunch Money.</li>
                <li><strong>Configure Categories:</strong> In Settings, categorize your transactions as Income, Debt, Non-Discretionary, or Discretionary.</li>
                <li><strong>Set Your Goals:</strong> Define financial goals (like Buffer amount) that will appear as horizontal lines on your projections.</li>
                <li><strong>View Your Planner:</strong> Navigate to the Planner page to see your cash flow projections.</li>
              </ol>
            </section>

            <section id="terms" style={{ marginTop: '40px' }}>
              <h2>Terms & Definitions</h2>

              <h3>Planner Table Columns</h3>
              <dl style={{ marginLeft: '20px' }}>
                <dt><strong>Date</strong></dt>
                <dd style={{ marginBottom: '15px', marginLeft: '20px' }}>The date that marks the start of each week in the projection (default: Sunday).</dd>

                <dt><strong>Income</strong></dt>
                <dd style={{ marginBottom: '15px', marginLeft: '20px' }}>Total recurring income for this week, calculated from recurring transactions or budgets categorized as Income. If the linked bank txn <strong>already cleared</strong> (on/before today), the row still appears when expanded but does not add to this column total (see <a href="#starting-balance-recurring" style={{ color: '#0366d6', textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}>Starting Balance &amp; Recurring Detail</a>). Ensure income categories, e.g., Salary, have been designated as Income category types.</dd>

                <dt><strong>Debt</strong></dt>
                <dd style={{ marginBottom: '15px', marginLeft: '20px' }}>Total debt payments for this week, including loan payments, credit card payments, and other debt obligations. Recurring rows whose matched txn <strong>already cleared</strong> are listed in the detail view but excluded from this total. Debt payments are typically reflected as positive value transactions in liability accounts, but are negative cashflow events, and should be categorized as such.</dd>

                <dt><strong>Non-Discretionary</strong></dt>
                <dd style={{ marginBottom: '15px', marginLeft: '20px' }}>Bills, necessities, and non-negotiable costs of services. Recurring rows whose matched txn <strong>already cleared</strong> appear in the detail table but do not add to this total.</dd>

                <dt><strong>Discretionary</strong></dt>
                <dd style={{ marginBottom: '15px', marginLeft: '20px' }}>Optional spending or negotiable amounts. Recurring rows whose matched txn <strong>already cleared</strong> appear in the detail table but do not add to this total.</dd>

                <dt><strong>Expense</strong></dt>
                <dd style={{ marginBottom: '15px', marginLeft: '20px' }}>The sum of Non-Discretionary + Discretionary + Debt for this week (excluding recurring amounts whose linked bank txn has <strong>already cleared</strong>).</dd>

                <dt><strong>Budgeted</strong></dt>
                <dd style={{ marginBottom: '15px', marginLeft: '20px' }}>Income - Expense. This is the projected net cash flow for this week (positive means money coming in, negative means money going out), after excluding recurring items whose linked txn already cleared.</dd>

                <dt><strong>Budgeted To Date</strong></dt>
                <dd style={{ marginBottom: '15px', marginLeft: '20px' }}>Cumulative sum of all Budgeted values from the start date to this week. This shows your running total of projected cash flow.</dd>

                <dt><strong>Adjustment</strong></dt>
                <dd style={{ marginBottom: '15px', marginLeft: '20px' }}>Manual adjustment to the budgeted net. Use this to account for one-time expenses, income changes, or other financial events, or scenario testing, not captured in your recurring transactions or budgets.</dd>

                <dt><strong>Adjusted Net</strong></dt>
                <dd style={{ marginBottom: '15px', marginLeft: '20px' }}>Budgeted + Adjustment. This is the weekly net cash flow for this week after accounting for manual adjustments.</dd>

                <dt><strong>Balance</strong></dt>
                <dd style={{ marginBottom: '15px', marginLeft: '20px' }}>Last week Balance + Adjusted Net. This is your projected account balance at the end of this week. Adjusted Net uses weekly Income and Expense, so it does not double-count cash whose bank txn <strong>already cleared</strong> before the projection started (already in account balances).</dd>

                <dt><strong>Balance-Buffer</strong></dt>
                <dd style={{ marginBottom: '15px', marginLeft: '20px' }}>Balance minus the buffer amount. This shows how much you have above your minimum comfortable balance (Buffer goal).</dd>
              </dl>

              <h3>Category Types</h3>
              <dl style={{ marginLeft: '20px' }}>
                <dt><strong>Income</strong></dt>
                <dd style={{ marginBottom: '15px', marginLeft: '20px' }}>Money coming into your account, such as salary, freelance income, or investment returns.</dd>

                <dt><strong>Debt</strong></dt>
                <dd style={{ marginBottom: '15px', marginLeft: '20px' }}>Payments toward debt obligations, including loans, credit cards, and other liabilities.</dd>

                <dt><strong>Non-Discretionary</strong></dt>
                <dd style={{ marginBottom: '15px', marginLeft: '20px' }}>Essential expenses that you must pay, such as rent, utilities, insurance, and required subscriptions.</dd>

                <dt><strong>Discretionary</strong></dt>
                <dd style={{ marginBottom: '15px', marginLeft: '20px' }}>Optional expenses that you can control or reduce, such as dining out, entertainment, shopping, and hobbies.</dd>
              </dl>

              <h3>Data Sources</h3>
              <dl style={{ marginLeft: '20px' }}>
                <dt><strong>Budgets</strong></dt>
                <dd style={{ marginBottom: '15px', marginLeft: '20px' }}>Monthly, weekly, or yearly budget amounts from Lunch Money. Budgets are pro-rated by week based on their granularity (weekly budgets appear as-is, monthly budgets are divided by ~4.33 weeks, yearly budgets by ~52 weeks).</dd>

                <dt><strong>Recurring Transactions</strong></dt>
                <dd style={{ marginBottom: '15px', marginLeft: '20px' }}>Recurring items from Lunch Money with expected dates (<strong>Rcr</strong> in detail tables). They can appear alongside budgets depending on your source priority in Settings.</dd>
              </dl>

              <h3>Other Terms</h3>
              <dl style={{ marginLeft: '20px' }}>
                <dt><strong>Buffer</strong></dt>
                <dd style={{ marginBottom: '15px', marginLeft: '20px' }}>Your minimum comfortable balance buffer. This is the amount you want to keep in your account as a safety cushion. The Balance-Buffer column shows how much you have above this buffer.</dd>

                <dt><strong>Starting Balance</strong></dt>
                <dd style={{ marginBottom: '15px', marginLeft: '20px' }}>The baseline before week-by-week projections: <strong>what&apos;s in the accounts now</strong>—Plaid <strong>cash/depository</strong> balances (excluding inactive/closed/problem accounts) plus <strong>manual cash assets</strong>. You can override the displayed starting balance in Settings.</dd>

                <dt><strong>Goals</strong></dt>
                <dd style={{ marginBottom: '15px', marginLeft: '20px' }}>Financial targets you set, such as emergency fund amounts or savings goals. Goals with names and values appear as horizontal orange lines on the cash flow visualization.</dd>

                <dt><strong>Adjustments</strong></dt>
                <dd style={{ marginBottom: '15px', marginLeft: '20px' }}>Manual changes to your projections to account for one-time expenses, income changes, or other financial events not captured in your recurring transactions or budgets.</dd>
              </dl>
            </section>

            <section id="starting-balance-recurring" style={{ marginTop: '40px' }}>
              <h2>Starting Balance & Recurring Detail</h2>

              <p>
                <strong>Starting balance</strong> is <strong>balances only</strong>: Plaid cash/depository account figures plus manual cash-type assets—i.e. what&apos;s already in the accounts. Meal Ticket does <strong>not</strong> run a separate bank-transaction fetch to inflate or adjust that number.
              </p>

              <p>
                Lunch Money&apos;s <strong>recurring items</strong> API supplies projected amounts and, when possible, <strong>matched bank transactions</strong> per occurrence. If a match <strong>already cleared</strong> (on or before today), counting it again in the week&apos;s totals would double-count cash that is already in your balances. Those lines use <strong>strikethrough</strong> and are <strong>excluded</strong> from weekly column totals and balance for that week.
              </p>

              <p>
                <strong>Weekly totals:</strong> Struck-through lines are visible for context (payee, category, <strong>Rcr</strong>) but their amounts are not added to Income, Debt, Non-Discretionary, Discretionary, Expense, Budgeted, Adjusted Net, or Balance. All other recurring lines <strong>do</strong> add to those totals. Budget-vs-recurring priority for a category is unchanged.
              </p>

              <p style={{ fontSize: '0.95em', color: '#444' }}>
                If a cleared line should be struck through but isn&apos;t, Lunch Money may not have attached a summarized transaction yet. If you disagree with the starting balance, use the override in Settings.
              </p>
            </section>

            <section id="features" style={{ marginTop: '40px' }}>
              <h2>Features</h2>

              <h3>Cash Flow Projections</h3>
              <p>Meal Ticket projects your account balance up to 75 weeks in advance, giving you a long-term view of your financial future. The planner table shows weekly projections, and the visualization chart provides a visual representation of your balance over time.</p>

              <h3>Multiple Data Sources</h3>
              <p>You can use budgets, recurring transactions, or both to generate projections. Budgets are pro-rated by week based on their granularity (weekly, monthly, or yearly), while recurring transactions show up on their specific dates.</p>

              <h3>Source Prioritization</h3>
              <p>In Settings, you can configure which data source takes precedence when both budgets and recurring transactions are available for the same category. Drag items to reorder them—the first item has the highest priority.</p>

              <h3>Goal Tracking</h3>
              <p>Set financial goals (like Buffer amount, emergency fund targets, or savings goals) and see them visualized as horizontal orange lines on your cash flow chart. This helps you see when your projected balance reaches or crosses these targets.</p>

              <h3>Category Management</h3>
              <p>Categorize your transactions as Income, Debt, Non-Discretionary, or Discretionary. This categorization affects how transactions appear in the planner table columns and helps you understand your spending patterns.</p>

              <h3>Manual Adjustments</h3>
              <p>Add one-time adjustments to fine-tune your projections. Adjustments can be positive (income) or negative (expenses) and can be associated with specific categories. Use adjustments to model scenarios without disrupting your carefully planned budget structure.</p>

              <h3>Detail Views</h3>
              <p>Expand any week row (or all rows) to see a detailed breakdown of transactions for each column. This shows payee, category, amount, and source (Bgt = Budget, Rcr = Recurring, Adj = Adjustment). <strong>Cleared</strong> recurring matches use <strong>strikethrough</strong> and do not add to column totals.</p>

              <h3>Visualization Chart</h3>
              <p>The interactive chart shows your projected balance over time, with goal lines displayed as horizontal orange lines. You can hover over data points to see detailed information, and the chart automatically scales to show your data clearly.</p>

              <h3>Week Start Day Configuration</h3>
              <p>Choose on which day of the week your planner weeks start (Sunday through Saturday). This affects how balances are calculated and displayed. For example, if Thursday is payday, select <strong>Thursday</strong>. Then your balance will more accurately reflect the income and expenses for the weekly period betwen paychecks. If you were to choose Sunday, instead, in this case, and incurred large expenses on Tuesday, the projected balance would not reflect the lowest value of the week, which would occur on Wednesday. If your bank account balance is low to begin with, critical information could be obscured by this misleading balance.</p>

              <h3>Multiple Currencies</h3>
              <p>Meal Ticket automatically displays amounts using your Lunch Money account's primary currency symbol. All calculations and displays respect your currency settings.</p>
            </section>

            <section id="tips" style={{ marginTop: '40px' }}>
              <h2>Tips & Best Practices</h2>

              <h3>Understanding Budgets vs. Recurring Transactions</h3>
              <p>There's an important distinction between recurring transactions and budgets:</p>
              <ul>
                <li><strong>Recurring transactions</strong> represent actual cash flow events that happen on specific dates—like a loan payment on the 15th of each month.</li>
                <li><strong>Budgets</strong> are typically pro-rated across a time period (weekly, monthly, or yearly). A large recurring transaction occurring in week 3 of a monthly budget, like a loan payment, impacts cash flow in a way that a monthly budget can't easily reflect.</li>
              </ul>
              <p>Meal Ticket handles both, allowing you to see the precise timing of recurring transactions alongside your budgeted amounts, giving you a more accurate picture of when money actually moves in and out of your account.</p>

              <h3>Using Adjustments Effectively</h3>
              <ul>
                <li>Use adjustments to model one-time expenses (like replacing a broken appliance) without permanently changing your budget.</li>
                <li>Account for expected income changes (bonuses, freelance work) that aren't in your recurring transactions.</li>
                <li>Model "what-if" scenarios to see how different financial decisions would impact your balance.</li>
                <li>Add notes to adjustments to remember why you made them.</li>
              </ul>

              <h3>Setting Up Categories</h3>
              <ul>
                <li>Be consistent with your categorization—it affects how transactions appear in the planner.</li>
                <li>Use Debt for all debt-related payments (loans, credit cards).</li>
                <li>Distinguish between Non-Discretionary (must pay) and Discretionary (optional) expenses to better understand your spending flexibility.</li>
                <li>Review and update category settings periodically as your financial situation changes.</li>
              </ul>

              <h3>Configuring Goals</h3>
              <ul>
                <li>Set your Buffer goal first—this represents your minimum comfortable balance.</li>
                <li>Add additional goals for specific financial milestones (emergency fund, vacation savings, etc.).</li>
                <li>Use goal lines on the chart to see when you'll reach important financial targets.</li>
                <li>Update goals as your financial situation evolves.</li>
              </ul>

              <h3>Source Order Priority</h3>
              <ul>
                <li>If you have both budgets and recurring transactions, put the more accurate source first.</li>
                <li>Recurring transactions are better for precise timing (like loan payments on specific dates).</li>
                <li>Budgets are better for general spending categories that don't have fixed dates.</li>
                <li>You can enable both and let Meal Ticket prioritize based on your preference.</li>
              </ul>

              <h3>Reviewing Your Projections</h3>
              <ul>
                <li>Check the Balance-Buffer column to see how much you have above your minimum buffer.</li>
                <li>Use the detail view (expand week rows) to understand what's contributing to each column.</li>
                <li>Struck-through recurring lines are for <strong>cleared</strong> matches already in account balances; column totals exclude them.</li>
                <li>Look for weeks where Balance-Buffer goes negative—these indicate potential cash flow issues.</li>
                <li>Review the visualization chart regularly to spot trends and patterns.</li>
              </ul>
            </section>

            <section id="how-to" style={{ marginTop: '40px' }}>
              <h2>How-To Guides</h2>

              <h3>How to Add an Adjustment</h3>
              <ol>
                <li>Navigate to the Adjustments page from the menu.</li>
                <li>Click the "Add Adjustment" button.</li>
                <li>Select the date for the adjustment (the Sunday date of the week).</li>
                <li>Optionally select a category to associate with the adjustment.</li>
                <li>Enter the amount and choose whether it's positive (+) or negative (-).</li>
                <li>Optionally add a note to remember why you made this adjustment.</li>
                <li>The adjustment will automatically appear in the Planner table for that week.</li>
              </ol>

              <h3>How to Categorize Transactions</h3>
              <ol>
                <li>Go to Settings.</li>
                <li>Scroll to the Category Settings section.</li>
                <li>For each category, select its type: Income, Debt, Non-Discretionary, or Discretionary.</li>
                <li>Categories default to Discretionary if not set.</li>
                <li>Changes are saved automatically and will affect future projections.</li>
                <li>Click "Reset to Defaults" to clear all category settings.</li>
              </ol>

              <h3>How to Set Up Goals</h3>
              <ol>
                <li>Go to Settings.</li>
                <li>Scroll to the Goals section.</li>
                <li>Enter a name for your goal (e.g., "Emergency Fund", "Vacation").</li>
                <li>Enter the target amount for that goal.</li>
                <li>Goals with both name and value will appear as horizontal orange lines on the chart.</li>
                <li>You can set up to 4 goals (including Buffer).</li>
                <li>Click the × button to clear a goal.</li>
              </ol>

              <h3>How to Change Source Priority</h3>
              <ol>
                <li>Go to Settings.</li>
                <li>Find the Sources section.</li>
                <li>Drag items up or down to reorder them.</li>
                <li>The first item has the highest priority—if both sources have data for the same category, the first one will be used.</li>
                <li>Changes take effect immediately and will recalculate your projections.</li>
              </ol>

              <h3>How to View Transaction Details</h3>
              <ol>
                <li>On the Planner page, find the week row you want to inspect.</li>
                <li>Click the expand icon (▶) on the left side of the row to expand it.</li>
                <li>You'll see detailed tables for each column showing individual transactions.</li>
                <li>Each transaction shows: Payee, Category, Amount, and Source (Bgt = Budget, Rcr = Recurring, Adj = Adjustment).</li>
                <li>Recurring rows with <strong>strikethrough</strong> mean the linked bank txn <strong>already cleared</strong>; amounts in those rows do not add to the parent column total.</li>
                <li>Click "Expand All" or "Collapse All" to expand or collapse all rows at once.</li>
              </ol>

              <h3>How to Change Your Week Start Day</h3>
              <ol>
                <li>Go to Settings.</li>
                <li>Find the "Start on Day of Week" dropdown in the General section.</li>
                <li>Select your preferred day (Sunday through Saturday).</li>
                <li>Your planner will automatically recalculate weeks starting on that day.</li>
              </ol>

              <h3>How to Override Starting Balance</h3>
              <ol>
                <li>Go to Settings.</li>
                <li>Find the "Starting Balance" field in the General section.</li>
                <li>Enter your desired starting balance amount.</li>
                <li>This will override the automatically calculated balance from your Lunch Money accounts.</li>
                <li>Use this if you want to start projections from a different balance than what's currently in your accounts.</li>
              </ol>
            </section>

            <div style={{ marginTop: '60px', paddingTop: '20px', borderTop: '2px solid #e1e4e8' }}>
              <p style={{ fontSize: '0.9em', color: '#666' }}>
                Need more help? Contact me at{' '}
                <a
                  href="mailto:mealticket-support@varontron.com"
                  style={{ color: '#0366d6', textDecoration: 'none' }}
                  onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                  onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                >
                  mealticket-support@varontron.com
                </a>
                {' '}or check out the <Link to={PAGE_PATHS.about} style={{ color: '#0366d6', textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}>About</Link> page for more information about Meal Ticket.
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (currentPage === 'about') {
      return (
        <div className="config-container">
          <div style={{ lineHeight: '1.6', color: '#24292e' }}>
            <h2>What is Meal Ticket?</h2>
            <p>
              Meal Ticket is a planning tool that helps you visualize your cash flow projections
              using data from <a href="https://lunchmoney.app/" target="_blank" rel="noopener noreferrer" style={{ color: '#0366d6', textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}>Lunch Money</a>. It provides weekly projections of your income, expenses, and
              account balance, helping you plan ahead and make informed financial decisions. You can fine-tune
              these projections with manual adjustments to account for one-time expenses, income changes, or other
              financial events not captured in your recurring transactions or budgets.
            </p>
            <p>
              Reality moves faster than your budget. Consider this scenario: your monthly budget accounts for
              regular expenses, but suddenly you need to replace a broken appliance or take an unexpected trip.
              These one-time expenses will impact your cash flow, and you need to see how they affect
              your balance weeks or months ahead—but you don't want to go through the hassle of
              permanently changing your budget just to account for a temporary situation or one-time event—just to change it back. With manual adjustments,
              you can model these scenarios without disrupting your carefully planned budget structure.
            </p>
            <p>
              There's an important distinction between recurring transactions and budgets. Recurring transactions
              represent actual cash flow events that happen on specific dates—like a loan payment on the 15th of
              each month. Budgets, on the other hand, are typically pro-rated across a time period (weekly, monthly,
              or yearly). A large recurring transaction occurring in week 3 of a monthly budget, like a loan payment,
              impacts cash flow in a way that a monthly budget can't easily reflect. The budget might show a smooth
              weekly distribution, but the reality is a significant cash outflow on a specific date. Meal Ticket
              handles both, allowing you to see the precise timing of recurring transactions alongside your budgeted
              amounts, giving you a more accurate picture of when money actually moves in and out of your account.
            </p>
            <p>
              Having a budget is important for financial health, but it is not itself a good indicator of it.
              A budget tells you what you plan to spend; cash flow projections show you what will actually
              happen. Meal Ticket bridges that gap, giving you the flexibility to see both your planned
              financial path and the reality of how temporary changes, unexpected expenses, or income and expense variations
              will impact your actual account balance over time. It's not that you wouldn't or shouldn't budget
              for these things—just that maybe you haven't—or that it isn't enough.
            </p>

            <h2>Getting Started</h2>
            <p>
              To get started, head to{' '}
              <Link
                to={PAGE_PATHS.settings}
                style={{ color: '#0366d6', textDecoration: 'none' }}
                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
              >
                Settings
              </Link>
              {' '}and enter your Lunch Money API key (or use <code style={{ backgroundColor: '#f6f8fa', padding: '2px 6px', borderRadius: '3px' }}>VITE_LUNCHMONEY_API_KEY</code> in <code style={{ backgroundColor: '#f6f8fa', padding: '2px 6px', borderRadius: '3px' }}>.env.local</code> when no key is saved—see{' '}
              <Link
                to={PAGE_PATHS.help}
                style={{ color: '#0366d6', textDecoration: 'none' }}
                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
              >
                Help &amp; Documentation
              </Link>
              ). That is the main setup page for connecting your data,
              configuring categories, setting goals, and preparing Meal Ticket for use.
            </p>

            <h2>Features</h2>
            <ul>
              <li><strong>Cash Flow Projections:</strong> See your projected balance up to 75 weeks in advance</li>
              <li><strong>Starting balance &amp; recurring alignment:</strong> Starting balance is <strong>account balances only</strong>. <strong>Cleared</strong> recurring matches show strikethrough in week detail and are omitted from weekly totals so the rollforward does not double-count cash already in balances</li>
              <li><strong>Multiple Data Sources:</strong> Use budgets and/or recurring transactions to generate projections. Budgets are pro-rated by week based on their granularity (weekly, monthly, or yearly)</li>
              <li><strong>Flexible Prioritization:</strong> Configure which data source takes precedence when both are available</li>
              <li><strong>Goal Tracking:</strong> Set financial goals and see them visualized as horizontal lines on your projections</li>
              <li><strong>Category Management:</strong> Categorize your transactions as Income, Debt, Non-Discretionary, or Discretionary</li>
              <li><strong>Manual Adjustments:</strong> Add one-time adjustments to fine-tune your projections</li>
              <li><strong>Detail Toggles:</strong> Expand any week row (or all rows) to see a detailed breakdown of transactions for each column</li>
              <li><strong>Multiple Currencies:</strong> Automatically displays amounts using your Lunch Money account's primary currency symbol</li>
            </ul>

            <h2>About the Developer</h2>
            <p>
              Engineer, compulsive DIYer, father of three.
            </p>

            <h2>Privacy</h2>
            <p>
              Meal Ticket is a client-side application that runs entirely in your browser. Your API key and
              financial data are stored locally in your browser's localStorage and are never transmitted to
              any server except <a href="https://lunchmoney.app/" target="_blank" rel="noopener noreferrer" style={{ color: '#0366d6', textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}>Lunch Money</a>'s official API when fetching your data.
            </p>
            <p>
              All data processing happens locally on your device. Only anonymous aggregate page views and
              visitor counts are monitored; no personal financial data is tracked or transmitted beyond
              requests to <a href="https://lunchmoney.app/" target="_blank" rel="noopener noreferrer" style={{ color: '#0366d6', textDecoration: 'none' }} onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'} onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}>Lunch Money</a>'s official API. Your privacy and data security are paramount.
            </p>

            <h2>Browser Support</h2>
            <p>
              Meal Ticket has been tested on Chrome, Firefox, and Safari. It should work on any modern browser
              that supports ES6+ JavaScript and localStorage.
            </p>

            <h2>Support</h2>
            <p>
              Need help or have questions? Contact me at{' '}
              <a
                href="mailto:mealticket-support@varontron.com"
                style={{ color: '#0366d6', textDecoration: 'none' }}
                onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
              >
                mealticket-support@varontron.com
              </a>
              .
            </p>
            <p>
              Enjoying Meal Ticket? Help keep it going by buying me a sandwich:
            </p>
            <a
              href={SANDWICH_SUPPORT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="save-button"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '4px',
                marginTop: '10px',
                width: '180px',
                padding: '8px 16px',
                fontSize: '14px',
                textDecoration: 'none',
                lineHeight: '1.5',
                boxSizing: 'border-box',
                whiteSpace: 'nowrap',
              }}
            >
              <span aria-hidden="true">🥪</span>
              <span>Buy me a Sandwich</span>
            </a>
          </div>
        </div>
      );
    }

    // Planner page (default)
    return (
      <>
        {loading && <div className="loading">Loading data...</div>}
        {error && <div className="error">Error: {error}</div>}

        {!loading && !error && weeks.length > 0 && (
          <PlannerTable
            weekStartDay={weekStartDay}
            onNavigateToAdjustments={(date) => {
              navigate(PAGE_PATHS.adjustments);
              // Store the date to scroll to/highlight later if needed
              // The adjustments component can check for this in localStorage or URL
              sessionStorage.setItem('adjustmentFocusDate', date);
            }}
            weeks={weeks}
            onAdjustmentChange={handleAdjustmentChange}
            bufferValue={bufferValue}
            startingBalance={startingBalance}
            goals={goals.filter(g => g.name && g.value !== null).map(g => ({ name: g.name, value: g.value! }))}
            currency={currency}
          />
        )}
      </>
    );
  };

  return (
    <div className="app">
      <header className="app-header">
        <div>
          <h1 style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img src="/meal-ticket-mascot.png" alt="Meal Ticket Mascot" className="mascot-icon" />
            <span style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: 0, lineHeight: '1.2', paddingTop: 0 }}>
              <span style={{ marginTop: 0, paddingTop: 0, lineHeight: '1.2' }}>Meal Ticket</span>
              <span style={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                Reality moves faster than your budget
              </span>
            </span>
          </h1>
        </div>
        <div className="header-controls">
          <NavLink to={PAGE_PATHS.help} className={({ isActive }) => `config-button${isActive ? ' active' : ''}`} title="Docs">
            📚 Docs
          </NavLink>
          <NavLink to={PAGE_PATHS.about} className={({ isActive }) => `config-button${isActive ? ' active' : ''}`} title="About">
            ℹ️ About
          </NavLink>
          <NavLink to={PAGE_PATHS.settings} className={({ isActive }) => `config-button${isActive ? ' active' : ''}`} title="Settings">
            ⚙️ Settings
          </NavLink>
          <NavLink to={PAGE_PATHS.adjustments} className={({ isActive }) => `config-button${isActive ? ' active' : ''}`}>
            📝 Adjustments
          </NavLink>
          {apiKey && (
            <NavLink to={PAGE_PATHS.planner} className={({ isActive }) => `config-button${isActive ? ' active' : ''}`} title="Planner">
              📅 Planner
            </NavLink>
          )}
        </div>
      </header>
      {renderPage() || <div className="config-container">Loading...</div>}
    </div>
  );
}

export default App;
